import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, LayoutGrid, FileDown, Star } from 'lucide-react';
import { moduleApi } from '../../../lib/api';
import { generateTableQrSheetPdf } from '../../../lib/tableQrSheetPdfGenerator';
import { TablesEditor, TableJourJ, trierTables } from './TablesEditor';

interface EvenementielEvent {
    id: string;
    type: 'PRIVÉ' | 'PROFESSIONNEL';
    first_name?: string;
    last_name?: string;
    company_name?: string;
    start_time: string;
}

interface Collecte {
    tables_planned_count: number;
    table_numbers: string[];
    vip_tables: string[];
    public_token: string;
}

const eventLabel = (e: EvenementielEvent) => e.type === 'PRIVÉ' ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : (e.company_name || '');

/** Page dédiée "Tables & QR codes (Jour J)" — sortie de la page de configuration du menu, qui devenait trop
 *  chargée pour un réglage utilisé un jour précis. Accessible via un bouton "QR codes" bien visible sur la
 *  page de suivi, au même niveau que "Mode terrain". Demande explicite d'Emeni (2026-08-22) : "cette partie
 *  là ne doit pas être dans les paramètres mais un bouton bien visible QR code". */
export function QrCodesPage() {
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();
    const [event, setEvent] = React.useState<EvenementielEvent | null>(null);
    const [collecte, setCollecte] = React.useState<Collecte | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [tablesDraft, setTablesDraft] = React.useState('');
    const [savingTables, setSavingTables] = React.useState(false);
    const [tablesDetail, setTablesDetail] = React.useState<TableJourJ[]>([]);
    const [vipTablesDraft, setVipTablesDraft] = React.useState<string[]>([]);
    const [newTableDraft, setNewTableDraft] = React.useState('');
    const [savingTableNumbers, setSavingTableNumbers] = React.useState(false);
    const [qrWidthValue, setQrWidthValue] = React.useState('7');
    const [qrHeightValue, setQrHeightValue] = React.useState('8.4');
    const [qrSizeUnit, setQrSizeUnit] = React.useState<'cm' | 'mm'>('cm');
    const [qrTitle, setQrTitle] = React.useState('DÉCLAREZ VOS RESTRICTIONS ALIMENTAIRES');
    const [generatingQr, setGeneratingQr] = React.useState(false);
    const [liveCheckins, setLiveCheckins] = React.useState<{ total_checked_in: number; tables: unknown[] } | null>(null);

    const load = React.useCallback(() => {
        if (!eventId) return;
        setLoading(true);
        Promise.all([
            moduleApi.getEvenementielEvent(eventId),
            moduleApi.getEvenementielCollecte(eventId),
        ]).then(([ev, col]) => {
            setEvent(ev);
            setCollecte(col);
            setTablesDraft(String(col?.tables_planned_count ?? 0));
            // Le serveur renvoie les fiches completes ; un etablissement qui n'a jamais ouvert cet ecran
            // n'a que des numeros, repris tels quels a la lecture.
            setTablesDetail(trierTables(Array.isArray(col?.tables) ? col.tables : []));
            setVipTablesDraft(Array.isArray(col?.vip_tables) ? col.vip_tables : []);
        }).catch((e: any) => setError(e?.message || 'Erreur de chargement.')).finally(() => setLoading(false));
    }, [eventId]);

    React.useEffect(() => { load(); }, [load]);

    React.useEffect(() => {
        if (!eventId || tablesDetail.length === 0) return;
        let cancelled = false;
        const poll = () => {
            moduleApi.getEvenementielCollecteLiveCheckins(eventId).then((d: any) => { if (!cancelled) setLiveCheckins(d); }).catch(() => {});
        };
        poll();
        const interval = setInterval(poll, 25000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [eventId, tablesDetail.length]);

    const saveTables = async () => {
        if (!eventId) return;
        setSavingTables(true);
        setError('');
        try {
            await moduleApi.updateEvenementielCollecte(eventId, { tables_planned_count: Number(tablesDraft) || 0 });
            load();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSavingTables(false);
        }
    };


    /** Enregistre aussitot : sur un ecran ou l'on cree seize tables d'un coup, un bouton « Enregistrer »
     *  oublie ferait perdre tout le plan de salle. */
    const enregistrerTables = async (nouvelles: TableJourJ[]) => {
        setTablesDetail(nouvelles);
        if (!eventId) return;
        setSavingTableNumbers(true);
        setError('');
        try {
            await moduleApi.updateEvenementielCollecte(eventId, { tables: nouvelles } as any);
            load();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement des tables.");
        } finally {
            setSavingTableNumbers(false);
        }
    };

    const handleGenerateQrSheet = async () => {
        if (!collecte?.public_token || tablesDetail.length === 0) return;
        setGeneratingQr(true);
        try {
            const unitFactor = qrSizeUnit === 'cm' ? 10 : 1;
            const cardWidthMm = (Number(qrWidthValue) || 7) * unitFactor;
            const cardHeightMm = (Number(qrHeightValue) || 8.4) * unitFactor;
            await generateTableQrSheetPdf({
                eventLabel: event ? eventLabel(event) : 'Privatisation',
                tables: tablesDetail.map((t) => t.numero),
                baseUrl: `${window.location.origin}/ma-table/${collecte.public_token}`,
                cardWidthMm,
                cardHeightMm,
                title: qrTitle,
            });
        } finally {
            setGeneratingQr(false);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    if (!event || !collecte) return <p className="text-sm text-content-muted">Événement introuvable.</p>;

    return (
        <div className="space-y-6 max-w-2xl">
            <div>
                <button onClick={() => navigate(`/menus-convives/${event.id}`)} className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content mb-3">
                    <ArrowLeft size={14} /> Retour au suivi
                </button>
                <h1 className="text-xl font-bold text-content tracking-tight">{eventLabel(event)} — Tables & QR codes</h1>
                <p className="text-content-muted text-sm mt-0.5">
                    {new Date(event.start_time).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
            </div>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            <div className="bg-surface-soft border border-border rounded-2xl p-4 space-y-3">
                <h3 className="text-sm font-bold text-content flex items-center gap-2"><LayoutGrid size={15} /> Tables</h3>
                <div className="flex items-center gap-2">
                    <input type="number" min={0} value={tablesDraft} onChange={(e) => setTablesDraft(e.target.value)}
                        className="w-24 bg-surface border border-border rounded-md px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                    <span className="text-xs text-content-muted">tables prévues pour le service</span>
                    <button onClick={saveTables} disabled={savingTables || Number(tablesDraft) === collecte.tables_planned_count}
                        className="ml-auto px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-xs disabled:opacity-40">
                        {savingTables ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
                <div className="pt-3 border-t border-border space-y-2">
                    <p className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Le plan de salle</p>
                    <p className="text-[11px] text-content-muted">
                        Un QR par table, jamais par convive : le même code sert à tous ceux qui s'y installent.
                        Le numéro est le vôtre — l'application ne renumérote rien.
                    </p>
                    <TablesEditor
                        tables={tablesDetail}
                        onChange={enregistrerTables}
                        onImprimer={handleGenerateQrSheet}
                        impressionEnCours={generatingQr}
                    />
                </div>
            </div>
        </div>
    );
}
